import React, { useState, useEffect } from 'react';
import { X, User, Music, Mail, Phone, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const CompleteProfilePrompt = ({ user, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    artist_name: user.artist_name || '',
    full_name: user.full_name || user.name || '',
    phone: user.phone || '',
    about_me: user.about_me || '',
    keep_name_private: user.keep_name_private || false
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSkip = () => {
    // Mark as "skipped for now" in localStorage
    localStorage.setItem('profile_prompt_skipped', 'true');
    onClose();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const response = await axios.put(`${API}/users/profile`, {
        ...formData,
        email: user.email
      });
      
      // Update local storage with new info
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      
      // Mark profile as complete
      localStorage.setItem('profile_completed', 'true');
      
      toast.success('Profile completed!');
      onComplete(updatedUser);
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleGoToProfile = () => {
    localStorage.setItem('profile_prompt_skipped', 'true');
    navigate('/profile');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-full">
              <AlertCircle size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Complete Your Profile</h2>
              <p className="text-purple-100 text-sm">Help us serve you better!</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <p className="text-blue-300 text-sm">
              <strong>⚡ Faster Checkout:</strong> Your information will be saved and automatically filled in during booking checkout, making your next session reservation quick and effortless!
            </p>
          </div>

          {/* Artist Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <Music size={16} className="text-purple-400" />
              Artist Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="artist_name"
              value={formData.artist_name}
              onChange={handleChange}
              placeholder="Your stage/artist name"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <User size={16} className="text-purple-400" />
              Full Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Your legal/full name"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
            
            {/* Name Display Preview */}
            <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-xs text-blue-300 mb-2"><strong>📋 Receipts will show:</strong></p>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  id="show_full_name_prompt"
                  checked={!formData.keep_name_private}
                  onChange={(e) => setFormData({ ...formData, keep_name_private: !e.target.checked })}
                  className="w-4 h-4 text-purple-600 bg-gray-800 border-purple-500 rounded"
                />
                <label htmlFor="show_full_name_prompt" className="text-xs text-gray-300 cursor-pointer">
                  Show both names on receipts
                </label>
              </div>
              <div className="p-2 bg-black/40 rounded text-white text-sm">
                {!formData.keep_name_private && formData.artist_name && formData.full_name
                  ? `${formData.artist_name} (${formData.full_name})`
                  : formData.artist_name || 'Artist Name'}
              </div>
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <Phone size={16} className="text-purple-400" />
              Phone Number <span className="text-red-400">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* About Me */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <User size={16} className="text-purple-400" />
              About Me <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <textarea
              name="about_me"
              value={formData.about_me}
              onChange={handleChange}
              placeholder="Tell us about your music style, achievements, etc..."
              rows={4}
              className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors resize-none"
            />
          </div>

          {/* Email Display */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Mail size={16} />
              <span>Email:</span>
              <span className="text-white font-semibold">{user.email}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving || !formData.artist_name || !formData.full_name || !formData.phone}
              className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
            <button
              onClick={handleGoToProfile}
              className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
            >
              Go to Profile Page
            </button>
            <button
              onClick={handleSkip}
              className="sm:w-auto px-6 py-3 text-gray-400 hover:text-white transition-colors"
            >
              Skip for Now
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            You can always update this later from your profile page
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfilePrompt;
